import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import { comnEnvs, comnUtils } from "@/comn/utils";
import {
    useForm,
    useAuth,
    useFetch,
    useResource,
    useGrid,
    useModal,
    useStore,
    useToast,
    usePopup,
    usePage,
} from "@/comn/hooks"; // hook
import {
    APIS,
    SF_MANL_INFO_SEARCH_FORM,
    SG_MANL_INFO_LIST,
    URLS,
} from "@/tz/go/egaz/osci/ptl/clntsport/manl/services/OsciManlService";
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/*
 * Screen Manual Information List
 * !== 화면 매뉴얼 정보 목록 ==!
 */
export const OSCI0107001Q = (): JSX.Element => {
    useResource({
        defaultSchema: [
            { area: "bsopClsfCd" },
            { area: "comnCd", comnCd: "OSC0088" },
            { area: "comnCd", comnCd: "OSC0095" },
        ],
    });

    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0001", "auth_osci0002", "auth_osci0003"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변화 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        manlSrch: useForm({
            defaultSchema: SF_MANL_INFO_SEARCH_FORM,
            defaultValues: {
                ...(pgeStore?.form || {}),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의  ==!
     */
    const grid = {
        manlSrchList: useGrid({
            defaultSchema: SG_MANL_INFO_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        getManlList: useFetch({
            api: (page = grid.manlSrchList.page) => {
                form.manlSrch.setValue("menuId", menuId);
                return APIS.getManlList(form.manlSrch.getValues(), page, grid.manlSrchList.size);
            },
            enabled: comnUtils.isEmpty(form.manlSrch.errors),
            key: [grid.manlSrchList.page, grid.manlSrchList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.manlSrch.getValues(),
                    page: grid.manlSrchList.page,
                    size: grid.manlSrchList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00043" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        getManlList: () => {
            grid.manlSrchList.setPage(0);
            fetch.getManlList.fetch(0);
        },
        onClear: () => {
            form.manlSrch.clearValues();
            form.manlSrch.setValues({
                bsopClsfCd: "OSC",
                ptlTpCd: "O",
                scrnId: "",
                scrnNm: "",
            });
        },
    };

    /*
     * Declaration Event Render
     * !== 랜더 정의 ==!
     */
    const render = {
        grid_manlSrchList: {
            cell: {
                scrnId: (props: any) => {
                    const { rowValues, value } = props;
                    return <Link to={`${URLS.osci0107003s}/${rowValues.scrnId}`}>{value}</Link>;
                },
            },
        },
    };

    useEffect(() => {
        // Service Category, Portal Type 은 고정값이므로 수정할 수 없도록 한다.
        form.manlSrch.setEditable("bsopClsfCd", false);
        // Portal Type 을 표시하지 않는 메뉴는 조회 조건도 그대로 두어 기존 동작을 유지한다.
        if (menuId != "menu_ptli0021") {
            if (comnUtils.isEmpty(form.manlSrch.getValue("ptlTpCd"))) {
                form.manlSrch.setValue("ptlTpCd", "O");
            }
            form.manlSrch.setEditable("ptlTpCd", false);
        }

        handler.getManlList();
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.manlSrch.schema.bsopClsfCd} />
                            {menuId == "menu_ptli0021" ? null : <Group.Control {...form.manlSrch.schema.ptlTpCd} />}
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.manlSrch.schema.scrnId} />
                            <Group.Control {...form.manlSrch.schema.scrnNm} />
                        </Group.Row>
                    </Group.Section>

                    <Layout direction="row">
                        <Layout.Left>
                            <Button role={"reset"} onClick={() => handler.onClear()} />
                        </Layout.Left>
                        <Layout.Right>
                            <Button role={"search"} onClick={handler.getManlList} />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Group.Section>
                        <Grid
                            {...grid.manlSrchList.grid}
                            data={fetch.getManlList.data?.manlList}
                            render={render.grid_manlSrchList}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout direction="row">
                <Layout.Left>
                    <Button
                        hidden={menuId != "menu_osci0148"}
                        role="new"
                        onClick={() => {
                            navigate(URLS.osci0107002s);
                        }}
                    />
                </Layout.Left>
            </Layout>
        </Page>
    );
};
